import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/contexts/AuthContext";
import { MapTrifold, Drop, UsersThree, Clock, ArrowRight, Plus, Crown, Warning, Upload, Sparkle } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    axios.get(`${API}/app/summary`).then((r) => setSummary(r.data));
    axios.get(`${API}/app/notifications/subscription`).then((r) => setNotifications(r.data?.notifications || []));
  }, []);

  const sub = user?.subscription;
  const isFree = sub?.tier === "free";
  const nearLimit =
    isFree && (sub?.applications_remaining ?? 99) <= 1;
  const isEmpty = summary && (summary.fields ?? 0) === 0 && (summary.applications ?? 0) === 0;

  return (
    <div className="max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-terracotta mb-2">
            Plantus Web
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest-900">
            Hallo {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="mt-2 font-body text-[15px] text-[#4A524C]">
            {user?.company_name} · {user?.role === "owner" ? "Inhaber" : "Mitarbeiter"}
          </p>
        </div>
        <Link
          to="/app/applications/new"
          data-testid="dash-new-application"
          className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium px-5 py-3 rounded-xl shadow-sm transition hover:-translate-y-0.5"
        >
          <Plus size={18} weight="bold" />
          Neue Anwendung
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <StatCard
          icon={MapTrifold}
          label="Flächen"
          value={summary?.fields ?? ","}
          to="/app/fields"
          testId="stat-fields"
        />
        <StatCard
          icon={Drop}
          label="Aufzeichnungen"
          value={summary?.applications ?? ","}
          to="/app/applications"
          testId="stat-applications"
        />
        <StatCard
          icon={UsersThree}
          label="Team-Mitglieder"
          value={summary?.members ?? ","}
          to="/app/team"
          testId="stat-members"
        />
      </div>

      {/* Subscription banners */}
      {(nearLimit || notifications.length > 0 || isEmpty) && (
        <div className="mt-6 space-y-3">
          {isEmpty && (
            <Link
              to="/app/import"
              data-testid="dash-import-banner"
              className="block rounded-2xl border border-forest-200 bg-gradient-to-r from-forest-50 to-bone hover:from-forest-100 px-5 py-4 transition"
            >
              <div className="flex items-center gap-3">
                <Sparkle size={22} weight="fill" className="text-terracotta" />
                <div className="flex-1">
                  <div className="font-heading font-semibold text-forest-900">
                    Schon Daten in einer Excel-Liste? Jetzt importieren!
                  </div>
                  <div className="text-sm text-[#4A524C]">
                    Plantus liest deine Liste automatisch aus und übernimmt Flächen oder Anwendungen mit einem Klick.
                  </div>
                </div>
                <Upload size={20} weight="bold" className="text-forest-700" />
              </div>
            </Link>
          )}
          {nearLimit && (
            <Link
              to="/preise"
              data-testid="dash-upgrade-banner"
              className="block rounded-2xl border border-forest-200 bg-forest-50 px-5 py-4 hover:bg-forest-100 transition"
            >
              <div className="flex items-center gap-3">
                <Crown size={22} weight="fill" className="text-terracotta" />
                <div className="flex-1">
                  <div className="font-heading font-semibold text-forest-900">
                    {sub?.applications_remaining === 0
                      ? "Free-Limit erreicht"
                      : `Noch ${sub?.applications_remaining} Anwendung${sub?.applications_remaining === 1 ? "" : "en"} im Free Tarif`}
                  </div>
                  <div className="text-sm text-[#4A524C]">
                    Upgrade auf Plantus Pro fuer unbegrenzte Aufzeichnungen und BVL-Sync.
                  </div>
                </div>
                <ArrowRight size={16} weight="bold" className="text-forest-700" />
              </div>
            </Link>
          )}
          {notifications.map((n, i) => (
            <Link
              key={i}
              to="/app/abo"
              data-testid="dash-renewal-banner"
              className={`block rounded-2xl border px-5 py-4 transition ${
                n.severity === "warning"
                  ? "border-amber-300 bg-amber-50 hover:bg-amber-100"
                  : "border-forest-200 bg-forest-50 hover:bg-forest-100"
              }`}
            >
              <div className="flex items-center gap-3">
                <Warning size={22} weight="fill" className={n.severity === "warning" ? "text-amber-700" : "text-forest-700"} />
                <div className="flex-1">
                  <div className="font-heading font-semibold text-forest-900">{n.message}</div>
                  <div className="text-sm text-[#4A524C]">Zum Verlaengern hier klicken.</div>
                </div>
                <ArrowRight size={16} weight="bold" className="text-forest-700" />
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-bone-200 p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={18} weight="duotone" className="text-forest-700" />
            <h2 className="font-heading text-lg font-semibold text-forest-900">
              Letzte Anwendung
            </h2>
          </div>
          {summary?.last_application ? (
            <div data-testid="last-application">
              <div className="font-heading text-xl font-medium text-forest-900">
                {summary.last_application.product_name}
              </div>
              <div className="mt-1 font-body text-sm text-[#4A524C]">
                {summary.last_application.field_name} ·{" "}
                {new Date(summary.last_application.applied_at).toLocaleString("de-DE")}
              </div>
              <div className="mt-3 font-body text-sm text-[#4A524C]">
                {summary.last_application.dosage} {summary.last_application.dosage_unit}{" "}
                {summary.last_application.active_ingredient
                  ? `· ${summary.last_application.active_ingredient}`
                  : ""}
              </div>
            </div>
          ) : (
            <div className="font-body text-sm text-[#7A827C]">
              Noch keine Anwendung erfasst.{" "}
              <Link to="/app/applications/new" className="text-forest-700 font-medium hover:underline">
                Jetzt erste Anwendung anlegen
              </Link>.
            </div>
          )}
        </div>

        <div className="bg-forest-700 text-white rounded-3xl p-6 sm:p-8">
          <h2 className="font-heading text-lg font-semibold">Export</h2>
          <p className="mt-2 font-body text-sm text-white/80">
            Bereit für die nächste Kontrolle? Exportieren Sie Ihre Aufzeichnungen
            als PDF oder JSON, exakt nach Vorgabe der Pflanzenschutzämter.
          </p>
          <Link
            to="/app/applications"
            className="mt-5 inline-flex items-center gap-2 text-white font-medium hover:text-terracotta transition"
          >
            Zur Übersicht <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, to, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group bg-white rounded-3xl border border-bone-200 p-6 hover:border-forest-100 hover:shadow-sm transition flex items-center gap-5"
  >
    <div className="w-12 h-12 rounded-2xl bg-forest-50 border border-forest-100 flex items-center justify-center">
      <Icon size={22} weight="duotone" className="text-forest-700" />
    </div>
    <div className="flex-1">
      <div className="font-body text-xs uppercase tracking-[0.16em] text-[#7A827C]">
        {label}
      </div>
      <div className="font-heading text-3xl font-semibold text-forest-900 leading-tight">
        {value}
      </div>
    </div>
    <ArrowRight
      size={16}
      weight="bold"
      className="text-[#7A827C] group-hover:text-forest-700 transition"
    />
  </Link>
);
